import { useState } from 'react';
import {
  Box, Typography, Container, Grid, Tabs, Tab, TextField,
  InputAdornment, Chip, Skeleton, Alert, MenuItem, Select,
  FormControl, InputLabel,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import EventCard from '../../components/events/EventCard';
import { PageHeader } from '../../components/shared';
import eventsService from '../../services/eventsService';
import { colors, designTokens } from '../../theme/theme';
import { useTranslation } from 'react-i18next';
import { queryKeys } from '../../utils/queryKeys';

export default function EventsPage() {
  const { t } = useTranslation();

  const [tab, setTab] = useState<'all' | 'professional' | 'social' | 'learn'>('all');
  const [search, setSearch] = useState('');
  const [zoneFilter, setZoneFilter] = useState('');
  const [sortBy, setSortBy] = useState('date');

  // Tab/search/sort filters are client-side (full week already fetched)
  // so they don't change the queryKey.
  const { data: events = [], isLoading: loading, error: eventsError } = useQuery({
    queryKey: queryKeys.events.weekly(),
    queryFn: ({ signal }) => eventsService.getWeekly(undefined, signal),
  });
  const { data: zones = [] } = useQuery({
    queryKey: queryKeys.events.zones(),
    queryFn: ({ signal }) => eventsService.getZones(signal),
  });
  const error = eventsError ? ((eventsError as Error).message || 'Failed to load events') : '';

  const filtered = events.filter(e => {
    if (tab === 'learn' && e.event_type !== 'masterclass' && e.event_type !== 'knowledge_session') return false;
    else if (tab !== 'all' && tab !== 'learn' && e.event_type !== tab) return false;
    if (zoneFilter && e.zone?.name !== zoneFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return e.title.toLowerCase().includes(q) || (e.description || '').toLowerCase().includes(q);
    }
    return true;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'popular': return (b.current_count || 0) - (a.current_count || 0);
      case 'price_asc': return (a.masterclass_fee || 0) - (b.masterclass_fee || 0);
      case 'price_desc': return (b.masterclass_fee || 0) - (a.masterclass_fee || 0);
      default: return new Date(a.scheduled_date).getTime() - new Date(b.scheduled_date).getTime();
    }
  });

  const open = filtered.filter(e => e.status === 'open');
  const confirmed = filtered.filter(e => e.status === 'confirmed');

  return (
    /* Dark Hero Page template (DESIGN_SYSTEM.md §Page Templates) —
       matches HomePage; padding = designTokens.spacing.page. */
    <Box sx={{ position: 'relative', minHeight: '100vh' }}>
      {/* Ambient warm-brown glow — matches HomePage hero */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 0,
          pointerEvents: 'none',
          background: `
            radial-gradient(ellipse 70% 45% at 15% 0%, rgba(180,100,60,0.18) 0%, transparent 65%),
            radial-gradient(ellipse 50% 40% at 85% 10%, rgba(138,92,246,0.08) 0%, transparent 60%)
          `,
        }}
      />

      <Container
        maxWidth="lg"
        sx={{
          position: 'relative',
          zIndex: 1,
          px: designTokens.spacing.page,
          py: { xs: 4, md: 6 },
        }}
      >
        <PageHeader
          title={t('events.this_week')}
          subtitle={t('events.browse')}
          mb={4}
        />

        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)}>
            <Tab label={t('events.all')} value="all" />
            <Tab label={t('events.professional')} value="professional" />
            <Tab label={t('events.social')} value="social" />
            <Tab label={t('events.learn')} value="learn" />
          </Tabs>
          <Box sx={{ flex: 1 }} />
          <TextField size="small" placeholder={t('events.search')} value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
            sx={{ minWidth: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('events.filter_zone')}</InputLabel>
            <Select value={zoneFilter} label="Zone" onChange={(e) => setZoneFilter(e.target.value as string)}>
              <MenuItem value="">All Zones</MenuItem>
              {zones.map(z => <MenuItem key={z.id} value={z.name}>{z.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Sort By</InputLabel>
            <Select value={sortBy} label="Sort By" onChange={(e) => setSortBy(e.target.value as string)}>
              <MenuItem value="date">Date (default)</MenuItem>
              <MenuItem value="popular">Most Popular</MenuItem>
              <MenuItem value="price_asc">Price: Low to High</MenuItem>
              <MenuItem value="price_desc">Price: High to Low</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Active filter chips */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Chip label={`${open.length} open`} color="primary" size="small" variant="outlined" />
          <Chip label={`${confirmed.length} confirmed`} color="success" size="small" variant="outlined" />
          {search && <Chip label={`"${search}"`} onDelete={() => setSearch('')} size="small" />}
          {zoneFilter && <Chip label={zoneFilter} onDelete={() => setZoneFilter('')} size="small" />}
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Grid container spacing={3}>
            {[1, 2, 3, 4, 5, 6].map(i => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Skeleton variant="rounded" height={280} sx={{ borderRadius: `${designTokens.radius.lg}px` }} />
              </Grid>
            ))}
          </Grid>
        ) : filtered.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 700 }}>No events match your filters</Typography>
            <Typography variant="body2" sx={{ color: colors.neutral[500], mt: 1 }}>
              Try adjusting your search or removing filters
            </Typography>
          </Box>
        ) : (
          <>
            {open.length > 0 && (
              <>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#FFFFFF', mb: 2, mt: 1 }}>
                  Open — Join Before Wednesday Midnight
                </Typography>
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  {open.map(event => (
                    <Grid item xs={12} sm={6} md={4} key={event.id}>
                      <EventCard event={event} />
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
            {confirmed.length > 0 && (
              <>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#FFFFFF', mb: 2 }}>
                  Confirmed — Tables Are Set
                </Typography>
                <Grid container spacing={3}>
                  {confirmed.map(event => (
                    <Grid item xs={12} sm={6} md={4} key={event.id}>
                      <EventCard event={event} />
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
          </>
        )}
      </Container>
    </Box>
  );
}
